import "./Tooltip.scss";

import React, {
	CSSProperties,
	useCallback,
	useContext,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from "react";
import Tippy, { TippyProps } from "@tippyjs/react";
import {
	followCursor as followCursorPlugin,
	Instance,
	Placement,
} from "tippy.js";
import clsx from "clsx";
import type { Modifier as BaseModifier } from "@popperjs/core";

import * as Flags from "../../../lib/Flags";
import * as Utils from "../../../lib/util/Utils";

import Button from "../Button/Button";
import { VisibleContext } from "../../../lib/util/ReactUtils";
import { AboveNavContext } from "../../nav/Navigation/Navigation";
import { useCallableEffect } from "../../../lib/hook/MiscHooks";
import { PropsOf } from "../../../lib/types/UtilTypes";

type PopperOptions = TippyProps["popperOptions"];

export type TooltipFollowCursor = "horizontal" | "vertical" | boolean;

export type TooltipPlacement = Placement;

export type TooltipTrigger = "mouseenter" | "focus" | "click";

export interface TooltipHandle {
	setVisible: (visible: boolean) => void;
	toggleVisible: () => void;
	visible: boolean;
}

export interface TooltipProps
	extends Omit<PropsOf<"div">, "ref" | "id" | "onSelect" | "onSelectCapture"> {
	content: React.ReactNode;
	children?: React.ReactNode;
	className?: string;
	tooltipClassName?: string;
	referenceClassName?: string;
	triggers?: TooltipTrigger[];
	showArrow?: boolean;
	followCursor?: TooltipFollowCursor;
	placement?: TooltipPlacement;
	onVisibleChange?: (visible: boolean) => void;
	distance?: number;
	overflow?: boolean;
	visible?: boolean;
	disabled?: boolean;
	overlay?: boolean | { tooltipReference: React.ReactNode };
	style?: CSSProperties;
	maxWidth?: number;
	scrollIntoView?: boolean;
	mobileDisabled?: boolean;
	fillWidth?: boolean;
	delay?: TippyProps["delay"];
	preventOverflow?: boolean;
}

const TOOLTIP_DELAY: [number, number] = [50, 0];
const TOOLTIP_ARROW = `<svg width="24" height="9">
<path d="M0 9s2.694-.02 7.005-5.422C8.777 1.35 10.395.009 12 0c1.605-.009 3.222 1.33 5.015 3.578C21.349 9.008 24 9 24 9z"/>
</svg>`;

const updateElementClass = (element: Element, className: string) => {
	if (element.className !== className) {
		element.setAttribute("class", className);
	}
};

export type Modifier = BaseModifier<any, any>;

const getFillWidth = (state: any, options: any) => {
	let padding = 0;
	if (options.overlay) {
		const tooltipContent = state.elements.popper.children[0];
		const computedStyle =
			tooltipContent.currentStyle || window.getComputedStyle(tooltipContent);
		padding +=
			parseFloat(computedStyle.paddingLeft) +
			parseFloat(computedStyle.paddingRight);
	}
	return state.elements.reference.offsetWidth + padding;
};

const fillWidthModifier: Modifier = {
	name: "fillWidth",
	enabled: true,
	phase: "beforeWrite",
	requires: ["computeStyles"],
	fn: ({ state, options }) => {
		state.styles.popper.width = `${getFillWidth(state, options)}px`;
	},
};

const Tooltip: React.ForwardRefRenderFunction<TooltipHandle, TooltipProps> = (
	props,
	ref,
) => {
	const {
		content,
		children,
		className,
		tooltipClassName,
		followCursor,
		distance,
		showArrow,
		overflow,
		placement,
		onVisibleChange,
		referenceClassName,
		visible,
		disabled,
		overlay,
		triggers,
		maxWidth,
		scrollIntoView,
		mobileDisabled,
		fillWidth,
		delay,
		preventOverflow,
		...otherProps
	} = props;

	const aboveNav = useContext(AboveNavContext);
	const visibleContextState = useContext(VisibleContext);

	const hasBeenVisible = useRef(false);

	const instanceRef = useRef<Instance>();
	const [forceVisible, setForceVisible] = useState(false);
	const [stateVisible, setStateVisible] = useState(false);
	const [currentPlacement, setCurrentPlacement] = useState(placement);

	useEffect(() => {
		if (!scrollIntoView) return;
		const timeoutId = setTimeout(() => {
			const element = instanceRef.current!.popper!;
			const headerOffset = 140;
			const elementPosition = Utils.getAbsolutePosition(element).top;

			window.scrollTo({
				top: elementPosition - headerOffset,
				behavior: "smooth",
			});
		}, 500);
		return () => clearTimeout(timeoutId);
	}, [stateVisible, scrollIntoView]);

	const toggleForceVisible = useCallback((e: any) => {
		e.preventDefault();
		e.stopPropagation();
		setForceVisible((visible) => !visible);
	}, []);

	let currentlyVisible = forceVisible
		? forceVisible
		: visible !== undefined
		? visible
		: stateVisible;
	if (!visibleContextState || disabled) {
		currentlyVisible = false;
	}

	if (currentlyVisible && !hasBeenVisible.current) {
		hasBeenVisible.current = true;
	}

	const updateElements = useCallableEffect(
		(callbackInstance?: Instance) => {
			const instance = instanceRef.current || callbackInstance;
			if (instance) {
				updateElementClass(
					instance.popper,
					clsx(
						"Tooltip__tooltip-wrapper",
						"tippy-popper",
						currentlyVisible && "--visible",
						aboveNav && "--aboveNav",
					),
				);

				const tooltip = instance.popper.children[0];
				if (tooltip) {
					updateElementClass(
						tooltip,
						clsx(
							"tippy-childContent",
							"Tooltip__tooltip",
							"tippy-box",
							currentlyVisible && "--visible",
							className,
							tooltipClassName,
						),
					);

					const content = tooltip.children[0];
					updateElementClass(
						content,
						clsx(
							"tippy-content",
							"Tooltip__tooltip-content",
							currentlyVisible && "--visible",
						),
					);
				}
			}
		},
		[aboveNav, className, currentlyVisible, tooltipClassName],
	);

	const checkForUpdatePlacement = useCallback(() => {
		const instance = instanceRef.current;
		if (instance?.popperInstance?.state && currentlyVisible) {
			const newPlacement = instance.popperInstance.state.placement;
			if (newPlacement !== currentPlacement) {
				setCurrentPlacement(newPlacement);
			}
		}
	}, [currentPlacement, currentlyVisible]);

	const handleShow = useCallback(
		(instance: Instance) => {
			instanceRef.current = instance;
			checkForUpdatePlacement();

			if (mobileDisabled && Utils.isMobileDevice()) {
				return;
			}

			if (!currentlyVisible) {
				setStateVisible(true);
				if (onVisibleChange) {
					onVisibleChange(true);
				}
			}
		},
		[
			checkForUpdatePlacement,
			mobileDisabled,
			currentlyVisible,
			onVisibleChange,
		],
	);
	const handleHide = useCallback(
		(instance: Instance) => {
			instanceRef.current = instance;
			setStateVisible(false);
			if (onVisibleChange) {
				onVisibleChange(false);
			}
		},
		[onVisibleChange],
	);

	const debug = Flags.isTooltipDebugMode();

	const currentPlacementHasVariants = ["start", "end"].some((variant) =>
		currentPlacement?.includes(variant),
	);

	const resolvedTriggers = triggers ? triggers.join(" ") : "mouseenter focus";

	const setVisible = useCallback((visible: boolean) => {
		if (instanceRef.current) {
			if (visible) {
				instanceRef.current!.show();
			} else {
				instanceRef.current!.hide();
			}
		}
	}, []);

	const tooltipHandle = useRef<TooltipHandle>({} as any);
	Object.assign(tooltipHandle.current, {
		visible: currentlyVisible,
		setVisible: setVisible,
		toggleVisible: () => {
			setVisible(!currentlyVisible);
		},
	});
	useImperativeHandle(ref, () => tooltipHandle.current);

	const childContent = (
		<div
			data-triggers={
				process.env.NODE_ENV === "development" ? resolvedTriggers : undefined
			}
			className={clsx(
				className,
				referenceClassName,
				"Tooltip",
				"Tooltip__reference",
				currentlyVisible && "--visible",
				disabled && "--inactive",
			)}
			onClick={otherProps.onClick ? otherProps.onClick : undefined}
			{...otherProps}
		>
			{debug && (
				<div className={"Tooltip__debug"}>
					<Button onClick={toggleForceVisible} className={"--dev"}>
						{forceVisible ? "Force Show ✔️" : "Force Show ❌"}
					</Button>
				</div>
			)}
			{children}
		</div>
	);

	const popperOptions = useMemo<PopperOptions>(() => {
		return {
			strategy: aboveNav ? "fixed" : "absolute",
			modifiers: [
				fillWidth && ({ ...fillWidthModifier, options: { overlay } } as any),
				{
					enabled: true,
					name: "offset",
					options: {
						offset: ({ reference }: any) => {
							if (!overlay) {
								const value = Number(overlay ? 0 : distance || 12);
								return [
									currentPlacementHasVariants
										? 12 * (currentPlacement?.includes("start") ? -1 : 1)
										: 0,
									value,
								];
							}

							const instance = instanceRef.current!;

							const referenceChild = instance.reference.children[0] as any;

							let computedDistance =
								(isNaN(referenceChild.offsetTop)
									? referenceChild.getBoundingClientRect().y
									: referenceChild.offsetTop) - reference.height;

							computedDistance -=
								(
									instance.popper.querySelector(".tippy-content")?.children[0]
										.children[0] as any
								)?.offsetTop || 0;

							return [0, computedDistance];
						},
					},
				},
			].filter<Modifier>(Boolean as any),
		} as PopperOptions;
	}, [
		aboveNav,
		fillWidth,
		overlay,
		distance,
		currentPlacementHasVariants,
		currentPlacement,
	]);

	const tippyContent = useMemo(() => {
		const overlayTrigger = overlay
			? typeof overlay === "boolean"
				? children
				: overlay.tooltipReference
			: null;

		return (
			<VisibleContext.Provider value={currentlyVisible}>
				{overlayTrigger}
				{content}
			</VisibleContext.Provider>
		);
	}, [overlay, children, currentlyVisible, content]);

	const currentArrow = showArrow === false || overlay ? false : TOOLTIP_ARROW;

	const handleUpdate = useCallback(
		(instance: Instance) => {
			instanceRef.current = instance;
			checkForUpdatePlacement();
		},
		[checkForUpdatePlacement],
	);

	const handleMount = useCallback(
		(instance: Instance) => {
			instanceRef.current = instance;
			updateElements(instance);
		},
		[updateElements],
	);

	return (
		<Tippy
			popperOptions={popperOptions}
			theme={"light"}
			onHide={handleHide}
			onMount={handleMount}
			onShow={handleShow}
			placement={placement}
			arrow={currentArrow}
			interactive={true}
			onBeforeUpdate={handleUpdate}
			interactiveBorder={7}
			delay={delay || TOOLTIP_DELAY}
			trigger={visible !== undefined ? undefined : resolvedTriggers}
			animation={"fade"}
			followCursor={followCursor}
			plugins={[followCursorPlugin]}
			disabled={!visibleContextState}
			visible={forceVisible ? true : visible}
			content={tippyContent}
			appendTo={overflow ? document.body : "parent"}
			maxWidth={maxWidth === undefined ? 460 : maxWidth}
			onClickOutside={handleHide}
		>
			{childContent}
		</Tippy>
	);
};

export default React.memo(React.forwardRef(Tooltip));
